import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ToastDock } from "./toast-dock.js";

/**
 * The one place an action says it happened, or why it did not (`toast` in
 * DESIGN.md).
 *
 * It started with two moments. Deleting questions closed the dialog and the
 * rows simply vanished, and a refused workspace rename quietly put the old
 * name back (AGENTS.md §1). Since 2026-09-29 it carries every action result in
 * the app: the grey "Saved" lines and red banners that did that job inside the
 * page are gone, and actions that said nothing now say something
 * (plans/toasts.md).
 *
 * A notice still describes a page's data — could not load, nothing yet. That
 * is not an action, and it has to stay where the data would be, because a
 * toast leaves in five seconds and the empty box would not.
 *
 * State lives in a provider because the message has to outlive what raised it:
 * the dialog that deleted the rows is unmounted by the time the toast is read.
 */
export type ToastTone = "done" | "error" | "info";

export interface ToastAction {
  readonly label: string;
  readonly run: () => void;
}

export interface ToastInput {
  readonly title: string;
  /** A second line: the detail, or the server's refusal in its own words. */
  readonly detail?: string;
  readonly action?: ToastAction;
}

export interface Toast extends ToastInput {
  readonly id: number;
  readonly tone: ToastTone;
}

export interface Toaster {
  readonly done: (input: string | ToastInput) => void;
  readonly info: (input: string | ToastInput) => void;
  readonly error: (input: string | ToastInput) => void;
  /** An action failed: `title` names it, and the error's own words follow. */
  readonly failed: (title: string, error: unknown) => void;
}

/** Newest in front and two behind; a fourth pushes the oldest out. */
const MOST_SHOWN = 3;

const ToastContext = createContext<Toaster | null>(null);

function reasonOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  // Held in state so both keep one identity for the provider's life: a caller
  // that lists the toaster as a dependency must not re-run on every toast.
  const [toaster] = useState<Toaster>(() => {
    let next = 0;
    const push = (tone: ToastTone, input: string | ToastInput): void => {
      next += 1;
      const body = typeof input === "string" ? { title: input } : input;
      const toast: Toast = { ...body, id: next, tone };
      setToasts((was) => [...was, toast].slice(-MOST_SHOWN));
    };
    return {
      done: (input) => {
        push("done", input);
      },
      info: (input) => {
        push("info", input);
      },
      error: (input) => {
        push("error", input);
      },
      failed: (title, error) => {
        push("error", { title, detail: reasonOf(error) });
      },
    };
  });
  const [close] = useState(() => (id: number) => {
    setToasts((was) => was.filter((toast) => toast.id !== id));
  });

  return (
    <ToastContext.Provider value={toaster}>
      {children}
      {toasts.length === 0 ? null : <ToastDock toasts={toasts} onClose={close} />}
    </ToastContext.Provider>
  );
}

/** The app's toaster. Outside a provider it throws, rather than showing nothing. */
export function useToast(): Toaster {
  const toaster = useContext(ToastContext);
  if (toaster === null) {
    throw new Error("useToast needs a ToastProvider above it");
  }
  return toaster;
}

/**
 * An error that arrived with the page, such as a refused Google sign-in read
 * from the address the API redirected to. Said once per message: a ref, not
 * state, so StrictMode's second mount does not say it twice.
 */
export function useErrorOnArrival(title: string, reason: string | null): void {
  const toast = useToast();
  const said = useRef<string | null>(null);
  // Raising a toast, not fetching: the kind of effect §15a allows.
  useEffect(() => {
    if (reason === null || said.current === reason) return;
    said.current = reason;
    toast.error({ title, detail: reason });
  }, [title, reason, toast]);
}

/**
 * For a dialog's `onInteractOutside`: a click on a toast is not a click away
 * from the dialog. Without it, closing an error toast raised by a dialog also
 * closed the dialog, and the person's input went with it.
 */
export function staysOpenForToasts(event: {
  readonly target: EventTarget | null;
  readonly preventDefault: () => void;
}): void {
  if (event.target instanceof Element && event.target.closest(".toast-dock") !== null) {
    event.preventDefault();
  }
}
